<template>
  <footer class="footer" v-if="establishment">
    <Container>
      <div class="footer__main">
        <Row>
          <Column class="col-lg-3">
            <div class="footer__nav">
              <b class="establishment-title">{{ establishment.title }}</b>
              <div class="address">
                {{ establishment.location.street }}<br>
                {{ establishment.location.zipcode }} {{ establishment.location.city.name }}<br>
                {{ establishment.phonenumber }}
              </div>
            </div>
          </Column>

          <Column class="col-lg-3">
            <template v-if="takeAwayHours">
              <div class="footer__nav">
                <b>{{ t('establishment_contact_info.takeaway_hours') }}</b>
                <ul>
                  <OpeningHours :opening-hours="takeAwayHours"/>
                </ul>
              </div>
            </template>
          </Column>

          <Column class="col-lg-3">
            <template v-if="deliveryHours">
              <b>{{ t('establishment_contact_info.delivery_hours') }}</b>
              <ul>
                <OpeningHours :opening-hours="deliveryHours"/>
              </ul>
            </template>
          </Column>

          <Column class="col-lg-3 brand">
            <div class="footer__nav footer__nav--social">
              <img alt="Bistroo"
                   src="/assets/img/brand/bistroo-logo.svg"
                   width="100"
              >
            </div>
            <Breadcrumbs class="mt-4"/>
          </Column>
        </Row>
      </div>

      <div class="footer__terms">
        <ul class="nav">
          <li class="nav__item">
            <RouterLink :to="localizedRoute({ name: 'terms' })">
              {{ t('nav.terms-and-conditions') }}
            </RouterLink>
          </li>
          <li class="nav__item">
            <RouterLink :to="localizedRoute({ name: 'privacy' })">
              {{ t('nav.privacy-statement') }}
            </RouterLink>
          </li>
          <li class="nav__item">
            <RouterLink :to="localizedRoute({ name: 'cookie' })">
              {{ t('nav.cookie-statement') }}
            </RouterLink>
          </li>
          <li class="nav__item">
            &copy; {{ currentDate.getFullYear() }} - Bistroo -
            {{ t('footer.rights') }}
          </li>
          <li class="nav__item nav__item--version">
            v{{ appVersion }}
          </li>
        </ul>
      </div>
    </Container>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import { computed } from 'vue'
import { version } from '../../../../package.json'
import Breadcrumbs from '@/components/SEO/Breadcrumbs.vue'
import Column from '@/components/Structure/Column/Column.vue'
import Row from '@/components/Structure/Row/Row.vue'
import Container from '@/components/Structure/Container/Container.vue'
import OpeningHours from '@/components/Misc/OpeningHours.vue'
import { Distribution } from '@/enums/Distribution'
import type { Establishment } from '@/types/http/Establishment'

const { t } = useI18n()
const { localizedRoute } = useLocalizedRouter()

const props = defineProps<{
  establishment: Establishment
}>()

const currentDate = new Date()
const appVersion = version

const deliveryHours = computed(
    () => props.establishment.opening_hours[Distribution.Delivery],
)
const takeAwayHours = computed(
    () => props.establishment.opening_hours[Distribution.TakeAway],
)
</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.footer {
  $self:            &;
  padding-top:      2rem;
  background-color: #ffffff;

  .container {
    padding: 0 1.5rem;
  }

  @include lg-up {
    padding-top: 3rem;

    .container {
      padding: 0;
    }
  }

  &__reviews {
    padding-bottom: 3rem;
    @include dotted-border-bottom;

    @include lg-up {
      padding:          0;
      background-image: none;
    }
  }

  b {
    display:       block;
    font-size:     1.1rem;
    margin-bottom: 0.8rem;
  }

  .establishment-title {
    @include lg-down {
      font-size: 1.4rem;
    }
  }

  .opening-hours {
    margin-left: -1rem;
    max-width:   12rem;
  }

  .brand {
    @include lg-down {
      margin-top: 4rem;
    }
  }

  &__main {
    border-bottom: 1px solid var(--color-neutral-tertiary);
    padding:       0 0 6rem 0;
    position:      relative;
  }

  &__nav {
    .nav {
      list-style: none;
      padding:    0;
      margin:     0;

      &__item {
        margin: 0.875rem 0;
      }
    }

    @include xl-down {
      margin-bottom: 2rem;
    }

    &--social {
      .nav {
        margin:  0rem -0.3125rem 0 -0.3125rem;
        display: flex;

        &__link {
          margin:          0 0.3125rem;
          border-radius:   50%;
          border:          1px solid var(--color-neutral-tertiary);
          height:          2.5rem;
          width:           2.5rem;
          display:         flex;
          align-items:     center;
          justify-content: center;
        }

        &__link {
          img {
            display: block;
          }
        }
      }
    }
  }

  &__terms {
    padding:         3rem 0;
    display:         block;
    justify-content: space-between;
    align-items:     center;
    font-size:       $font-size-sm;

    @include lg-up {
      display: flex;
    }

    .nav {
      display:    flex;
      flex-wrap:  wrap;
      margin:     0 -0.5rem 2rem -0.5rem;
      list-style: none;
      padding:    0;

      @include lg-up {
        margin: 0 -0.5rem;
      }

      &__item {
        padding:     0.5rem;
        width:       50%;
        flex-shrink: 0;

        &--version {
          color: var(--color-neutral-tertiary);
        }

        @include lg-up {
          margin:      0 0.5rem;
          padding:     0;
          width:       auto;
          flex-shrink: 1;
        }
      }
    }
  }
}
</style>
